import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Image,
  Users,
  MessageSquare,
  LogOut,
  Menu,
  X,
  Save,
  Upload,
  Trash2,
  Plus,
  Check,
  AlertCircle,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('content');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Content state
  const [content, setContent] = useState({});
  
  // Images state
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', password: '' });
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [expandedContact, setExpandedContact] = useState(null);

  const token = localStorage.getItem('adminToken');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      const [contentRes, imagesRes, usersRes, contactsRes] = await Promise.all([
        axios.get(`${API}/content`),
        axios.get(`${API}/admin/images`, headers),
        axios.get(`${API}/admin/users`, headers),
        axios.get(`${API}/admin/contacts`, headers)
      ]);
      setContent(contentRes.data);
      setImages(imagesRes.data);
      setUsers(usersRes.data);
      setContacts(contactsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  // Content handlers
  const handleContentChange = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, content, authHeaders);
      showMessage('success', 'Content saved successfully!');
    } catch (err) {
      showMessage('error', 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  // Image handlers
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/admin/upload`, formData, {
        ...authHeaders,
        headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' }
      });
      showMessage('success', 'Image uploaded successfully!');
      loadData();
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      await axios.delete(`${API}/admin/images/${imageId}`, authHeaders);
      showMessage('success', 'Image deleted');
      loadData();
    } catch (err) {
      showMessage('error', 'Failed to delete image');
    }
  };

  // User handlers
  const createUser = async () => {
    if (!newUser.username || !newUser.password) {
      showMessage('error', 'Please fill in all fields');
      return;
    }
    try {
      await axios.post(`${API}/admin/users`, newUser, authHeaders);
      showMessage('success', 'User created successfully!');
      setNewUser({ username: '', password: '' });
      loadData();
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to create user');
    }
  };

  const deleteUser = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${username}`, authHeaders);
      showMessage('success', 'User deleted');
      loadData();
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Contact handlers
  const markContactRead = async (contactId) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}/read`, {}, authHeaders);
      loadData();
    } catch (err) {
      showMessage('error', 'Failed to mark as read');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`, authHeaders);
      showMessage('success', 'Message deleted');
      loadData();
    } catch (err) {
      showMessage('error', 'Failed to delete message');
    }
  };

  const menuItems = [
    { id: 'content', label: 'Edit Content', icon: FileText },
    { id: 'images', label: 'Images', icon: Image },
    { id: 'users', label: 'Admin Users', icon: Users },
    { id: 'contacts', label: 'Messages', icon: MessageSquare, badge: contacts.filter(c => !c.read).length }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'content':
        return (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-navy">Edit Website Content</h2>
              <Button onClick={saveContent} disabled={saving} className="bg-red-600 hover:bg-red-700">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>

            {/* Company Info */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold text-navy mb-4">Company Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Company Name</Label>
                  <Input value={content.company_name || ''} onChange={(e) => handleContentChange('company_name', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={content.phone || ''} onChange={(e) => handleContentChange('phone', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={content.email || ''} onChange={(e) => handleContentChange('email', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Working Hours</Label>
                  <Input value={content.working_hours || ''} onChange={(e) => handleContentChange('working_hours', e.target.value)} className="mt-1" />
                </div>
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  <Input value={content.address || ''} onChange={(e) => handleContentChange('address', e.target.value)} className="mt-1" />
                </div>
              </div>
            </div>

            {/* Hero Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold text-navy mb-4">Hero Section (Home Page)</h3>
              <div className="space-y-4">
                <div>
                  <Label>Hero Title</Label>
                  <Input value={content.hero_title || ''} onChange={(e) => handleContentChange('hero_title', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Hero Subtitle</Label>
                  <Textarea value={content.hero_subtitle || ''} onChange={(e) => handleContentChange('hero_subtitle', e.target.value)} rows={3} className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Primary Button Text</Label>
                    <Input value={content.hero_primary_cta || ''} onChange={(e) => handleContentChange('hero_primary_cta', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label>Secondary Button Text</Label>
                    <Input value={content.hero_secondary_cta || ''} onChange={(e) => handleContentChange('hero_secondary_cta', e.target.value)} className="mt-1" />
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold text-navy mb-4">CTA Section (Bottom of Home Page)</h3>
              <div className="space-y-4">
                <div>
                  <Label>CTA Text</Label>
                  <Textarea value={content.cta_text || ''} onChange={(e) => handleContentChange('cta_text', e.target.value)} rows={2} className="mt-1" />
                </div>
                <div>
                  <Label>CTA Button Text</Label>
                  <Input value={content.cta_button_text || ''} onChange={(e) => handleContentChange('cta_button_text', e.target.value)} className="mt-1" />
                </div>
              </div>
            </div>

            {/* Contact Page */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold text-navy mb-4">Contact Page</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Page Title</Label>
                    <Input value={content.contact_title || ''} onChange={(e) => handleContentChange('contact_title', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label>Page Subtitle</Label>
                    <Input value={content.contact_subtitle || ''} onChange={(e) => handleContentChange('contact_subtitle', e.target.value)} className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label>Demo Scheduling Text</Label>
                  <Textarea value={content.demo_text || ''} onChange={(e) => handleContentChange('demo_text', e.target.value)} rows={3} className="mt-1" />
                </div>
              </div>
            </div>

            {/* Logo URL */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold text-navy mb-4">Logo</h3>
              <div>
                <Label>Logo URL</Label>
                <Input value={content.logo_url || ''} onChange={(e) => handleContentChange('logo_url', e.target.value)} className="mt-1" placeholder="Enter logo image URL or upload an image below" />
                {content.logo_url && (
                  <div className="mt-4 p-4 bg-navy rounded-lg inline-block">
                    <img src={content.logo_url} alt="Logo Preview" className="h-20 object-contain" />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'images':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-navy">Image Library</h2>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                <Button as="span" disabled={uploading} className="bg-red-600 hover:bg-red-700">
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Image'}
                </Button>
              </label>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image) => (
                <div key={image.id} className="bg-white rounded-xl p-3 shadow-sm border group">
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-3">
                    <img src={`${API}/images/${image.id}`} alt={image.filename} className="w-full h-full object-cover" />
                  </div>
                  <p className="text-sm text-navy truncate mb-2">{image.filename}</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-xs"
                      onClick={() => {
                        navigator.clipboard.writeText(`${API}/images/${image.id}`);
                        showMessage('success', 'URL copied!');
                      }}
                    >
                      Copy URL
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteImage(image.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {images.length === 0 && (
              <div className="text-center py-12 text-navy/60">
                <Image className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No images uploaded yet</p>
              </div>
            )}
          </div>
        );

      case 'users':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-navy">Admin Users</h2>

            {/* Create User */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold text-navy mb-4">Create New Admin</h3>
              <div className="flex gap-4">
                <Input
                  placeholder="Username"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                />
                <Button onClick={createUser} className="bg-red-600 hover:bg-red-700 whitespace-nowrap">
                  <Plus className="h-4 w-4 mr-2" /> Add User
                </Button>
              </div>
            </div>

            {/* Users List */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-navy">Username</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-navy">Created</th>
                    <th className="text-right px-6 py-3 text-sm font-semibold text-navy">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 text-navy font-medium">{user.username}</td>
                      <td className="px-6 py-4 text-navy/60 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteUser(user.username)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'contacts':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-navy">Contact Messages</h2>

            {contacts.length === 0 ? (
              <div className="text-center py-12 text-navy/60 bg-white rounded-xl">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No messages yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div key={contact.id} className={`bg-white rounded-xl shadow-sm border overflow-hidden ${!contact.read ? 'border-red-300' : ''}`}>
                    <div
                      className="p-4 cursor-pointer flex items-center justify-between"
                      onClick={() => {
                        setExpandedContact(expandedContact === contact.id ? null : contact.id);
                        if (!contact.read) markContactRead(contact.id);
                      }}
                    >
                      <div className="flex items-center gap-4">
                        {!contact.read && <div className="w-2 h-2 bg-red-600 rounded-full" />}
                        <div>
                          <p className="font-semibold text-navy">{contact.name}</p>
                          <p className="text-sm text-navy/60">{contact.email} • {contact.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-navy/40">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </span>
                        {expandedContact === contact.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </div>
                    </div>
                    {expandedContact === contact.id && (
                      <div className="px-4 pb-4 border-t">
                        <div className="pt-4">
                          {contact.company && <p className="text-sm text-navy/60 mb-2">Company: {contact.company}</p>}
                          <p className="text-navy whitespace-pre-wrap">{contact.message}</p>
                        </div>
                        <div className="mt-4 flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => window.location.href = `mailto:${contact.email}`}>
                            Reply via Email
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteContact(contact.id)}>
                            <Trash2 className="h-4 w-4 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-navy text-white px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <span className="font-semibold">Admin Panel</span>
        <button onClick={handleLogout}><LogOut className="h-5 w-5" /></button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-navy transition-transform duration-200`}>
          <div className="p-6">
            <h1 className="text-xl font-bold text-white">Admin Panel</h1>
            <p className="text-white/60 text-sm">United ELD LLC</p>
          </div>

          <nav className="px-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    activeTab === item.id ? 'bg-white/10 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </span>
                  {item.badge > 0 && (
                    <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          {message.text && (
            <div className={`mb-6 px-4 py-3 rounded-lg flex items-center gap-2 ${
              message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {message.type === 'success' ? <Check className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
              {message.text}
            </div>
          )}
          {renderContent()}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;
